import React from 'react';
import { Box, Typography, Tooltip, Chip } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { useTranslation } from 'react-i18next';
import {
  generateInsightText,
  getPerformanceHealth,
  getLossConcentration,
  formatCurrency,
  getCategoryBreakdownArray,
  deduplicateTopProblems
} from './aiInsightUtils';

/**
 * AI Summary Chip Component
 *
 * Displays a compact AI-generated insight with a rich tooltip
 * Matches the design from the plan exactly
 */
const AISummaryChip = ({ breakdown, totalCost, machineCount, experimentCount, groupLabel }) => {
  const { t } = useTranslation();

  // Generate the main insight text
  const insight = generateInsightText(breakdown, totalCost, machineCount, t);

  // Color schemes for different insight types (all light)
  const chipColors = {
    performance: {
      background: 'linear-gradient(135deg, #FEE2E2 0%, #FECACA 100%)',
      color: '#DC2626',
      border: '#FCA5A5',
      iconColor: '#DC2626',
      hoverBg: 'linear-gradient(135deg, #FECACA 0%, #FCA5A5 100%)',
      shadow: 'rgba(220, 38, 38, 0.2)'
    },
    concentration: {
      background: 'linear-gradient(135deg, #EDE9FE 0%, #DDD6FE 100%)',
      color: '#7C3AED',
      border: '#C4B5FD',
      iconColor: '#7C3AED',
      hoverBg: 'linear-gradient(135deg, #DDD6FE 0%, #C4B5FD 100%)',
      shadow: 'rgba(124, 58, 237, 0.2)'
    },
    category: {
      background: 'linear-gradient(135deg, #DBEAFE 0%, #BFDBFE 100%)',
      color: '#2563EB',
      border: '#93C5FD',
      iconColor: '#2563EB',
      hoverBg: 'linear-gradient(135deg, #BFDBFE 0%, #93C5FD 100%)',
      shadow: 'rgba(37, 99, 235, 0.2)'
    },
    urgency: {
      background: 'linear-gradient(135deg, #FEF3C7 0%, #FDE68A 100%)',
      color: '#D97706',
      border: '#FCD34D',
      iconColor: '#D97706',
      hoverBg: 'linear-gradient(135deg, #FDE68A 0%, #FCD34D 100%)',
      shadow: 'rgba(217, 119, 6, 0.2)'
    },
    normalized: {
      background: 'linear-gradient(135deg, #FEF9C3 0%, #FEF08A 100%)',
      color: '#A16207',
      border: '#FDE047',
      iconColor: '#CA8A04',
      hoverBg: 'linear-gradient(135deg, #FEF08A 0%, #FDE047 100%)',
      shadow: 'rgba(202, 138, 4, 0.2)'
    }
  };

  const colors = chipColors[insight.type] || chipColors.normalized;

  // Prepare tooltip data
  const performanceHealth = getPerformanceHealth(breakdown?.avgPerformanceRatio, t);
  const lossConcentration = getLossConcentration(breakdown?.topProblems, totalCost);
  const categoryBreakdown = getCategoryBreakdownArray(breakdown?.categoryBreakdown);
  const perMachineLoss = machineCount > 0 ? Math.round(totalCost / machineCount) : 0;

  // Deduplicate and calculate cumulative percentages for top problems
  const getTopProblemsWithCumulative = () => {
    if (!breakdown?.topProblems?.length) return [];

    // Deduplicate by product name, summing costs
    const uniqueProducts = deduplicateTopProblems(breakdown.topProblems);

    let cumulative = 0;
    return uniqueProducts.slice(0, 3).map((problem) => {
      const pct = totalCost > 0 ? (problem.cost / totalCost) * 100 : 0;
      cumulative += pct;
      return {
        ...problem,
        cumulativePercentage: Math.round(cumulative)
      };
    });
  };

  const topProblemsWithCumulative = getTopProblemsWithCumulative();

  // Tooltip content - matching plan design exactly
  const tooltipContent = (
    <Box sx={{
      p: 2,
      minWidth: 320,
      maxWidth: 360,
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif'
    }}>
      {/* Header with group name */}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.75,
        mb: 2
      }}>
        <AutoAwesomeIcon sx={{ fontSize: 16, color: '#7C3AED' }} />
        <Typography sx={{
          fontSize: '13px',
          fontWeight: 600,
          color: '#1F2937',
          letterSpacing: '-0.01em'
        }}>
          {t('aiAnalysisFor', { group: groupLabel, defaultValue: `AI Analysis: ${groupLabel}` })}
        </Typography>
      </Box>

      {/* Performance Health Section */}
      {breakdown?.avgPerformanceRatio !== null && breakdown?.avgPerformanceRatio !== undefined && (
        <Box sx={{ mb: 2 }}>
          <Typography sx={{
            fontSize: '11px',
            fontWeight: 600,
            color: '#374151',
            mb: 0.5
          }}>
            {t('performanceHealth', { defaultValue: 'Performance Health' })}
          </Typography>
          <Typography sx={{
            fontSize: '12px',
            color: '#4B5563',
            lineHeight: 1.4,
            mb: 0.75
          }}>
            {t('productsSellingAtBenchmark', {
              percentage: performanceHealth.percentage,
              defaultValue: `Products selling at ${performanceHealth.percentage}% of category benchmark`
            })}
          </Typography>
          {/* Progress bar - 10 blocks like in the plan: ████░░░░░░ */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ display: 'flex', gap: '2px' }}>
              {[...Array(10)].map((_, i) => (
                <Box
                  key={i}
                  sx={{
                    width: 16,
                    height: 8,
                    borderRadius: '2px',
                    bgcolor: i < Math.round(performanceHealth.percentage / 10)
                      ? performanceHealth.color
                      : '#E5E7EB'
                  }}
                />
              ))}
            </Box>
            <Typography sx={{
              fontSize: '10px',
              color: performanceHealth.color,
              fontWeight: 500,
              ml: 0.5
            }}>
              ({performanceHealth.label})
            </Typography>
          </Box>
        </Box>
      )}

      {/* Loss Concentration Section */}
      {topProblemsWithCumulative.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography sx={{
            fontSize: '11px',
            fontWeight: 600,
            color: '#374151',
            mb: 0.5
          }}>
            {t('lossConcentration', { defaultValue: 'Loss Concentration' })}
          </Typography>
          {lossConcentration.top3Percentage > 0 && (
            <Typography sx={{
              fontSize: '12px',
              color: '#4B5563',
              mb: 0.75
            }}>
              {t('top3AccountForLoss', {
                percentage: lossConcentration.formatted,
                defaultValue: `Top 3 problems account for ${lossConcentration.formatted} of total loss:`
              })}
            </Typography>
          )}
          {topProblemsWithCumulative.map((problem, idx) => (
            <Box key={idx} sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              py: 0.25
            }}>
              <Typography sx={{
                fontSize: '11px',
                color: '#4B5563',
                display: 'flex',
                alignItems: 'center'
              }}>
                <span style={{ marginRight: '6px', color: '#9CA3AF' }}>•</span>
                <span style={{
                  maxWidth: '140px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  display: 'inline-block'
                }}>
                  {problem.productName}
                </span>
              </Typography>
              <Typography sx={{
                fontSize: '11px',
                color: '#6B7280',
                fontFamily: 'monospace',
                letterSpacing: '-0.02em'
              }}>
                {formatCurrency(problem.cost)}
              </Typography>
            </Box>
          ))}
        </Box>
      )}

      {/* Category Pattern Section - explains most problems belong to these categories */}
      {categoryBreakdown.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography sx={{
            fontSize: '11px',
            fontWeight: 600,
            color: '#374151',
            mb: 0.5
          }}>
            {t('categoryPattern', { defaultValue: 'Category Pattern' })}
          </Typography>
          <Typography sx={{
            fontSize: '11px',
            color: '#6B7280',
            mb: 0.5
          }}>
            {t('categoryPatternExplanation', { defaultValue: 'Most problems belong to these categories:' })}
          </Typography>
          <Typography sx={{
            fontSize: '12px',
            color: '#4B5563',
            lineHeight: 1.4
          }}>
            {categoryBreakdown.slice(0, 3).map(cat => `${cat.name}: ${cat.percentage}%`).join(' | ')}
          </Typography>
        </Box>
      )}

      {/* Footer: Per-machine metric - simple, centered */}
      <Box sx={{
        pt: 1.5,
        borderTop: '1px solid #E5E7EB'
      }}>
        <Typography sx={{
          fontSize: '12px',
          fontWeight: 500,
          color: '#374151',
          textAlign: 'center'
        }}>
          {formatCurrency(perMachineLoss)} {t('avgLossPerMachineSimple', { defaultValue: 'avg loss per machine' })}
        </Typography>
      </Box>
    </Box>
  );

  // Ring gauge component for performance ratio - always red
  const RingGauge = () => {
    const ratio = breakdown?.avgPerformanceRatio;
    if (ratio === null || ratio === undefined) return null;

    const pct = Math.min(Math.round(ratio * 100), 100);
    const size = 28;
    const strokeWidth = 3;
    const radius = (size - strokeWidth) / 2;
    const circumference = 2 * Math.PI * radius;
    const offset = circumference - (pct / 100) * circumference;

    // Always red
    const ringColor = '#DC2626';

    return (
      <Tooltip
        title={t('productVsCategoryExplanation', {
          percentage: pct,
          defaultValue: `Products selling at ${pct}% of category benchmark`
        })}
        arrow
        placement="top"
      >
        <span
          onClick={(e) => e.stopPropagation()}
          style={{
            position: 'relative',
            width: size,
            height: size,
            cursor: 'help',
            flexShrink: 0,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
          <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
            {/* Background circle */}
            <circle
              cx={size / 2}
              cy={size / 2}
              r={radius}
              fill="none"
              stroke="#FECACA"
              strokeWidth={strokeWidth}
            />
            {/* Progress circle */}
            <circle
              cx={size / 2}
              cy={size / 2}
              r={radius}
              fill="none"
              stroke={ringColor}
              strokeWidth={strokeWidth}
              strokeDasharray={circumference}
              strokeDashoffset={offset}
              strokeLinecap="round"
            />
          </svg>
          {/* Percentage text in center */}
          <span style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '9px',
            fontWeight: 700,
            color: ringColor,
            lineHeight: 1
          }}>
            {pct}
          </span>
        </span>
      </Tooltip>
    );
  };

  const chipElement = (
    <Tooltip
        title={tooltipContent}
        arrow
        placement="top"
        enterDelay={200}
        componentsProps={{
          tooltip: {
            sx: {
              bgcolor: '#FFFFFF',
              color: '#1F2937',
              borderRadius: '8px',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
              border: '1px solid #E5E7EB',
              borderLeft: '4px solid #7C3AED',
              p: 0,
              maxWidth: 'none'
            }
          },
          arrow: {
            sx: {
              color: '#FFFFFF',
              '&:before': {
                border: '1px solid #E5E7EB'
              }
            }
          }
        }}
      >
        <Chip
          icon={<AutoAwesomeIcon sx={{ fontSize: 12, color: `${colors.iconColor} !important` }} />}
          label={insight.text}
          size="small"
          onClick={(e) => e.stopPropagation()}
          sx={{
            background: colors.background,
            color: colors.color,
            fontSize: '11px',
            fontWeight: 500,
            height: '24px',
            cursor: 'pointer',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
            letterSpacing: '-0.01em',
            border: `1px solid ${colors.border}`,
            '& .MuiChip-icon': {
              marginLeft: '6px',
              marginRight: '-2px'
            },
            '& .MuiChip-label': {
              paddingLeft: '6px',
              paddingRight: '10px'
            },
            '&:hover': {
              background: colors.hoverBg,
              transform: 'translateY(-1px)',
              boxShadow: `0 2px 8px ${colors.shadow}`
            },
            transition: 'all 0.2s ease'
          }}
        />
    </Tooltip>
  );

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
      {chipElement}
      <RingGauge />
    </Box>
  );
};

export default AISummaryChip;
